'use client';

// Shared search field for admin-web and bidder-web (consolidated from the two
// per-app copies, BL-596 follow-up / sharing.md § 1).
//
// A text input styled as a search box: leading magnifier icon, a brand focus
// ring, a trailing clear button that resets the
// value via a synthetic change event (so a controlled `onChange` sees `''`)
// and refocuses, and a Cmd/Ctrl+A select-all shortcut (some embedded contexts
// swallow the native one). `loading` swaps the magnifier for a spinner to
// signal an in-flight query. Controlled via `value` / `onChange`.
//
// Two densities (BL-1255): `default` is the page-toolbar field every caller had
// before this prop existed; `compact` fits a ~30px console header rail. The
// geometry is resolved INSIDE the component rather than left to a caller's
// `className` — this file composes with plain `clsx`, which CONCATENATES rather
// than resolving conflicting Tailwind utilities, so a caller passing `py-1`
// alongside the built-in `py-2` would get whichever Tailwind's stylesheet emits
// later, not whichever was passed later. That is the BUG-1009 failure mode; the
// fix is the same one PanelHeader / Modal / EmptyState use — own the variant.

import { forwardRef, useCallback, useRef, type InputHTMLAttributes } from 'react';
import { Search, X, XCircle } from 'lucide-react';
import { Spinner } from './Spinner';
import clsx from 'clsx';

/** Field scale. `compact` targets a console header rail; `default` is the page-toolbar field. */
export type SearchInputDensity = 'default' | 'compact';

/**
 * Per-density geometry. Split into position / glyph / field so the icon, the
 * clear button, and the input can each take only the part they need.
 */
const DENSITY: Record<
    SearchInputDensity,
    { iconLeft: string; clearRight: string; glyph: string; field: string; padClear: string; padBare: string }
> = {
    default: {
        iconLeft: 'left-3',
        clearRight: 'right-3',
        glyph: 'h-5 w-5',
        field: 'rounded-lg py-2 pl-10',
        padClear: 'pr-10',
        padBare: 'pr-4',
    },
    compact: {
        iconLeft: 'left-2',
        clearRight: 'right-2',
        glyph: 'h-[13px] w-[13px]',
        field: 'h-[26px] rounded-md py-0 pl-8 text-[12px]',
        padClear: 'pr-8',
        padBare: 'pr-2',
    },
};

/**
 * Per-density chrome — the resting border/background and the focus treatment.
 *
 * Both densities take their brand colour from the `primary` token rather than a
 * literal: bidder-web's tenant layout overrides `--primary` at runtime so
 * CSS-level chrome (accents, selection, focus rings) follows the tenant's brand,
 * and a hardcoded Auctogon teal was the one control escaping that white-label
 * intent (BL-1332). On admin-web `--primary` is the Auctogon teal, so nothing
 * there moves.
 *
 * `default` is the page-toolbar field: a grey hairline that grows a 2px brand
 * focus ring. `compact` (BL-1332 AC 9) is the console header field, which only
 * ever exists while EXPANDED — so teal is its resting state, not a focus state,
 * and it takes a 1px hairline instead. The 2px ring is deliberately absent
 * there: in the console's language 2px teal means "the selected catalog row",
 * so a ringed field reads as an error.
 */
const CHROME: Record<SearchInputDensity, { field: string; glyph: string }> = {
    default: {
        field:
            'border-gray-300 bg-white text-gray-700 placeholder-gray-400 ' +
            'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary',
        glyph: 'text-gray-400',
    },
    compact: {
        field:
            'border-primary bg-[var(--teal-50)] text-[var(--ink-900)] ' +
            'placeholder-[var(--ink-400)] focus:outline-none',
        glyph: 'text-primary',
    },
};

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'placeholder'> {
    /**
     * The hint. Required, no default (ADR 059 — Search Spine § 2.4): it lists
     * exactly what the box matches, and only the surface that owns the search
     * knows that — a shared default is a second copy of one surface's promise.
     */
    placeholder: string;
    /** Classes on the relative wrapper (width, margin). */
    containerClassName?: string;
    /** Show the in-field clear button when there's a value (default true). */
    clearable?: boolean;
    /** Called when the clear button is pressed (after the value is reset). */
    onClear?: () => void;
    /** Swap the magnifier for a spinner to signal an in-flight query. */
    loading?: boolean;
    /** Field scale. Defaults to `default` — existing callers are unaffected. */
    density?: SearchInputDensity;
}

/**
 * A search-styled text input. Extra props spread onto the underlying
 * `<input type="search">`.
 *
 * @param props - {@link SearchInputProps}.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
    {
        className,
        containerClassName,
        clearable = true,
        onClear,
        loading = false,
        density = 'default',
        value,
        onChange,
        placeholder,
        'aria-label': ariaLabel,
        ...props
    },
    forwardedRef,
) {
    const innerRef = useRef<HTMLInputElement>(null);
    const setRefs = useCallback(
        (node: HTMLInputElement | null) => {
            innerRef.current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
        },
        [forwardedRef],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
                e.preventDefault();
                innerRef.current?.select();
                return;
            }
            props.onKeyDown?.(e);
        },
        [props],
    );

    const handleClear = useCallback(() => {
        if (onChange) {
            const synthetic = {
                target: { value: '' },
                currentTarget: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(synthetic);
        }
        onClear?.();
        innerRef.current?.focus();
    }, [onChange, onClear]);

    const d = DENSITY[density];
    const chrome = CHROME[density];
    const hasValue = value != null && String(value).length > 0;
    const showClear = clearable && hasValue;

    return (
        <div className={clsx('relative', containerClassName)}>
            {loading ? (
                <span
                    className={clsx('absolute top-1/2 -translate-y-1/2 text-primary', d.iconLeft, d.glyph)}
                >
                    <Spinner size="sm" />
                </span>
            ) : (
                <Search
                    className={clsx(
                        'pointer-events-none absolute top-1/2 -translate-y-1/2',
                        chrome.glyph,
                        d.iconLeft,
                        d.glyph,
                    )}
                    aria-hidden="true"
                />
            )}
            <input
                ref={setRefs}
                type="search"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                aria-label={ariaLabel ?? placeholder}
                {...props}
                onKeyDown={handleKeyDown}
                className={clsx(
                    'w-full border',
                    chrome.field,
                    d.field,
                    showClear ? d.padClear : d.padBare,
                    // Hide the native WebKit search clear affordance — we render our own.
                    '[&::-webkit-search-cancel-button]:hidden',
                    className,
                )}
            />
            {showClear && (
                <button
                    type="button"
                    onClick={handleClear}
                    aria-label="Clear search"
                    className={clsx(
                        'absolute top-1/2 -translate-y-1/2 transition-colors',
                        density === 'compact'
                            ? 'text-primary hover:text-[var(--brand-ink)]'
                            : 'text-gray-400 hover:text-gray-600',
                        d.clearRight,
                    )}
                >
                    {/* The compact field's clear is a bare ✕ per BL-1332 AC 9 —
                        the filled XCircle is a second weight competing with the
                        26px field it sits inside. */}
                    {density === 'compact' ? (
                        <X className={d.glyph} strokeWidth={2} aria-hidden="true" />
                    ) : (
                        <XCircle className={d.glyph} aria-hidden="true" />
                    )}
                </button>
            )}
        </div>
    );
});
